import { api } from "@/services/omnimizer-api-service";
import type {
  ObservabilityResponse,
  OmnimizerCluster,
  OmnimizerDeployment,
  PredefinedDeployment,
} from "./types";

const OMNIMIZER_PATH =
  (typeof window !== "undefined" && window.env?.REACT_APP_OMNIMIZER_PATH) ||
  "/omnimizer-api";

const path = (suffix: string) => `${OMNIMIZER_PATH}${suffix}`;

export const omnimizerKeys = {
  deployments: ["omnimizer", "deployments"] as const,
  clusters: ["omnimizer", "clusters"] as const,
  predefined: ["omnimizer", "predefined"] as const,
  observability: (id: string, version: number, hours: number) =>
    ["omnimizer", "observability", id, version, hours] as const,
};

export const omnimizerApi = {
  listDeployments: async () =>
    (await api.get<OmnimizerDeployment[]>(path("/deployments/"))).data,
  findDuplicates: async (modelUrl: string) =>
    (await api.get<import("./types").DeploymentDuplicate[]>(path("/deployments/duplicates"), { params: { model_url: modelUrl } })).data,
  listClusters: async () =>
    (
      await api.get<OmnimizerCluster[]>(path("/clusters/"), {
        params: { active_only: false },
      })
    ).data,
  listPredefined: async () =>
    (
      await api.get<PredefinedDeployment[]>(path("/predefined-deployments/"), {
        params: { active_only: true },
      })
    ).data,
  createDeployment: async (payload: unknown) =>
    (await api.post<OmnimizerDeployment>(path("/deployments/"), payload)).data,
  checkAdmission: async (payload: unknown) =>
    (await api.post(path("/deployments/admission"), payload)).data,
  start: async (id: string) => (await api.post(path(`/deployments/${encodeURIComponent(id)}/resume`))).data,
  stop: async (id: string) => (await api.post(path(`/deployments/${encodeURIComponent(id)}/pause`))).data,
  runtimeStatus: async (id: string) =>
    (await api.get<{ pending_reason?: import("./types").PendingReason; started_at?: string | null }>(path(`/deployments/${encodeURIComponent(id)}/runtime-status`))).data,
  createPredefined: async (payload: unknown) =>
    (await api.post<PredefinedDeployment>(path("/predefined-deployments/"), payload)).data,
  observability: async (d: Pick<OmnimizerDeployment, "model_id" | "version">, hours: number) =>
    (
      await api.get<ObservabilityResponse>(
        path(`/deployments/${encodeURIComponent(d.model_id)}/observability`),
        { params: { version: d.version, hours } },
      )
    ).data,
  createApiKey: async (deploymentId: string, userId: string, description: string) =>
    (
      await api.post<{ id: number | string; key: string }>(
        path(`/deployments/${encodeURIComponent(deploymentId)}/keys`),
        null,
        { params: { user_id: userId, description } },
      )
    ).data,
};
